// src/components/checkout/SuccessHandler.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { verifyCheckoutSession } from '../../services/paymentService';
import { updateSubscription } from '../../services/subscriptionService';

/**
 * SuccessHandler component - processes successful Stripe checkout sessions
 * and updates the user's subscription status
 */
const SuccessHandler = () => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const { currentUser, updateUserTier, isAuthReady } = useAuth();
  
  useEffect(() => {
    // Parse query parameters
    const params = new URLSearchParams(window.location.search);
    const isCheckoutSuccess = params.get('checkout_success') === 'true';
    const sessionId = params.get('session_id');
    
    // If this isn't a checkout success redirect, skip processing
    if (!isCheckoutSuccess || !sessionId) {
      return;
    }

    // If auth isn't ready yet, don't start processing
    if (!isAuthReady) {
      console.log('Waiting for auth state...');
      return;
    }
    
    const processCheckoutSuccess = async () => {
      let retryCount = 0;
      const maxRetries = 3;
      
      const verifyWithRetry = async () => {
        try {
          setStatus('processing');
          
          // Make sure we have a user
          if (!currentUser) {
            throw new Error('User not authenticated');
          }
          
          console.log(`Processing checkout (attempt ${retryCount + 1}): ${sessionId} for user ${currentUser.uid}`);
          
          // Verify the checkout session
          const result = await verifyCheckoutSession(sessionId, currentUser.uid);
          
          if (!result) {
            throw new Error('No response from verification endpoint');
          }
          
          if (!result.success) {
            throw new Error(result.error || 'Verification failed');
          }
          
          if (!result.subscription) {
            throw new Error('No subscription data in response');
          }
          
          console.log('Checkout verified successfully:', result);
          
          // IMPORTANT: Force tier update in localStorage to prevent flicker
          localStorage.setItem('userTier', 'PRO');
          
          // Force update subscription in localStorage
          const subscriptionData = {
            status: result.subscription.status,
            currentPeriodEnd: result.subscription.currentPeriodEnd,
            planId: result.subscription.planId,
            id: result.subscription.id
          };
          
          console.log('Storing subscription data in localStorage:', subscriptionData);
          localStorage.setItem('userSubscription', JSON.stringify(subscriptionData));
          
          // Now update the application state
          try {
            // Update user tier to PRO in app state
            await updateUserTier('PRO');
            console.log('Updated user tier in auth context');
            
            // Update subscription in app state
            console.log('Updating subscription with:', subscriptionData);
            updateSubscription(subscriptionData);
          } catch (stateError) {
            console.error('Error updating application state:', stateError);
            // Continue anyway since we've updated localStorage
          }
          
          // Clear URL parameters
          window.history.replaceState({}, document.title, '/account');
          
          setStatus('success');
          
          // Force reload to avoid any Firebase offline issues
          console.log('Forcing complete page reload to refresh authentication state');
          setTimeout(() => {
            // Use reload with cache clearing
            window.location.href = '/account?upgraded=true&t=' + new Date().getTime();
          }, 1500);
          
          return true; // Success
        } catch (error) {
          console.error(`Verification attempt ${retryCount + 1} failed:`, {
            error,
            message: error.message,
            sessionId,
            userId: currentUser?.uid
          });
          
          if (retryCount < maxRetries) {
            retryCount++;
            // Wait 2 seconds before retrying
            await new Promise(resolve => setTimeout(resolve, 2000));
            return verifyWithRetry();
          }
          
          throw error; // Throw after all retries fail
        }
      };
      
      try {
        await verifyWithRetry();
      } catch (error) {
        console.error('All verification attempts failed:', error);
        setError(
          error.message === 'No response from verification endpoint'
            ? 'Unable to verify your subscription. Please try refreshing the page.'
            : error.message
        );
        setStatus('error');
      }

    };
    
    processCheckoutSuccess();
  }, [currentUser, updateUserTier, isAuthReady]);
  
  // If this isn't a checkout success page, don't render anything
  if (!new URLSearchParams(window.location.search).get('checkout_success')) {
    return null;
  }

  // Show loading state while waiting for auth
  if (!isAuthReady) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-white bg-opacity-90">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800">
            Preparing checkout verification...
          </h2>
        </div>
      </div>
    );
  }
  
  // If we haven't started processing yet, don't show anything
  if (!status) {
    return null;
  }
  
  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-[9999] bg-white bg-opacity-90"
    >
      {status === 'processing' && (
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800">
            Finalizing your Pro subscription...
          </h2>
        </div>
      )}
      
      {status === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-lg mx-auto shadow-lg">
          <h2 className="text-2xl font-bold text-green-700 mb-2">
            Congratulations! You're now a Pro subscriber!
          </h2>
          <p className="text-green-600">
            Your account has been upgraded. Redirecting you now...
          </p>
        </div>
      )}
      
      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg mx-auto shadow-lg">
          <h2 className="text-2xl font-bold text-red-700 mb-2">
            There was a problem activating your subscription
          </h2>
          <p className="text-red-600">
            {error || 'Please contact support if this issue persists.'}
          </p>
          <button 
            onClick={() => window.location.href = '/account'} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Return to Account
          </button>
        </div>
      )}
    </div>
  );
};

export default SuccessHandler;